/* ============================================================================
   DASHBOARD LAYOUT — CONSTANTS
   All configuration, navigation data, mock data and icon paths for the
   application shell live here so the JSX stays logic-only.
   ========================================================================== */

export const COLORS = {
  sidebar: "#590040bd",
  sidebarSolid: "#590040",
  sidebarHover: "rgba(255, 255, 255, 0.08)",
  sidebarActiveBg: "#FFFFFF",
  sidebarActiveText: "#000000",
  sidebarBorder: "rgba(255, 255, 255, 0.12)",
  sidebarSectionText: "rgba(255, 255, 255, 0.55)",
  white: "#FFFFFF",
  black: "#000000",
  background: "#FFFFFF",
  headerBorder: "#EDE7EC",
  textPrimary: "#1A1220",
  textSecondary: "#6B6270",
  textMuted: "#9C93A0",
  accent: "#590040",
  accentSoft: "#F4E9F1",
  danger: "#D64550",
  success: "#1E9E6B",
  overlay: "rgba(20, 8, 17, 0.45)",
  focusRing: "#590040",
};

export const BREAKPOINTS = {
  mobileSm: 375,
  mobileMd: 480,
  mobileLg: 640,
  tabletSm: 768,
  tabletLg: 1024,
  laptopSm: 1180,
  laptopLg: 1280,
  desktop: 1536,
};

export const SIDEBAR_WIDTH = 264;
export const SIDEBAR_WIDTH_COLLAPSED = 84;
export const HEADER_HEIGHT = 68;
/* Below this width, sidebar becomes a drawer */
export const DRAWER_BREAKPOINT = BREAKPOINTS.tabletLg;

export const NAVIGATION = [
  {
    section: "MAIN",
    items: [
      { label: "Dashboard", path: "/", icon: "dashboard", end: true },
      { label: "Community", path: "/community", icon: "community" },
      { label: "Connections", path: "/connections", icon: "connections" },
      { label: "Messages", path: "/messages", icon: "messages", badge: 3 },
      { label: "Events", path: "/events", icon: "events" },
      { label: "Blogs", path: "/blogs", icon: "blogs" },
    ],
  },
  {
    section: "BUSINESS",
    items: [
      { label: "Schedule Meeting", path: "/schedule-meeting", icon: "schedule" },
      { label: "Meeting Genie", path: "/meeting-genie", icon: "genie" },
      { label: "Shark Tank", path: "/shark-tank", icon: "sharktank" },
      { label: "Advertisements", path: "/advertisements", icon: "ads" },
      { label: "Image Optimizer", path: "/image-optimizer", icon: "image" },
    ],
  },
  {
    section: "ACCOUNT",
    items: [
      { label: "My Documents", path: "/documents", icon: "documents" },
      { label: "Payments", path: "/payments", icon: "payments" },
      { label: "Membership", path: "/membership", icon: "membership" },
    ],
  },
  {
    section: "SUPPORT",
    items: [
      { label: "Settings", path: "/settings", icon: "settings" },
      { label: "Help & Support", path: "/help", icon: "help" },
    ],
  },
];

export const PROFILE_MENU = [
  { label: "My Profile", desc: "Personal, professional and company profile", icon: "user", path: "/profile" },
  { label: "Account Settings", desc: "Account information and preferences", icon: "settings", path: "/settings" },
  { label: "Privacy & Security", desc: "Password, 2FA, visibility and blocked users", icon: "shield", path: "/security" },
  { label: "Notification Settings", desc: "Email, platform and business notifications", icon: "bell", path: "/notification-settings" },
  { label: "Membership", desc: "Current plan, benefits and upgrades", icon: "membership", path: "/membership" },
  { label: "Billing & Payments", desc: "Payment methods, transactions and invoices", icon: "payments", path: "/payments" },
  { label: "Help & Support", desc: "Help center, FAQs and support", icon: "help", path: "/help" },
];

export const MOCK_USER = {
  name: "Rahul Mehta",
  designation: "Premium Member",
  initials: "RM",
  avatarColor: "#590040",
};

export const MOCK_NOTIFICATIONS = [
  { id: 1, type: "connection", title: "New connection request", desc: "Aditi Sharma wants to connect with you", time: "5m ago", read: false },
  { id: 2, type: "meeting", title: "Meeting request", desc: "Karan Sethi requested a meeting on Thu, 3:00 PM", time: "22m ago", read: false },
  { id: 3, type: "meeting", title: "Meeting accepted", desc: "Your meeting with Neha Kapoor was accepted", time: "1h ago", read: false },
  { id: 4, type: "message", title: "New message", desc: "You have a new message from Vivek Rao", time: "2h ago", read: true },
  { id: 5, type: "ad", title: "Advertisement approved", desc: "Your listing \u201cSummer Expo 2026\u201d is now live", time: "4h ago", read: true },
  { id: 6, type: "payment", title: "Payment confirmation", desc: "Payment of \u20b94,999 received successfully", time: "1d ago", read: true },
  { id: 7, type: "event", title: "Event reminder", desc: "IHN Business Meetup starts tomorrow at 10:00 AM", time: "1d ago", read: true },
  { id: 8, type: "sharktank", title: "Shark Tank update", desc: "New investment offer received on your pitch", time: "2d ago", read: true },
];

/* Hand-authored, stroke-based SVG paths (no external icon library). */
export const ICON_PATHS = {
  dashboard: "M4 4h6v7H4V4zm10 0h6v4h-6V4zM4 15h6v5H4v-5zm10-6h6v11h-6V9z",
  community: "M8 11a3 3 0 100-6 3 3 0 000 6zm8 0a3 3 0 100-6 3 3 0 000 6zM2 20c0-3 2.7-5.4 6-5.4s6 2.4 6 5.4M12 20c0-2.6 2-4.7 4.5-5.2 2.6.4 5.5 2.5 5.5 5.2",
  connections: "M8 12a4 4 0 108 0 4 4 0 00-8 0zM2 12h3M19 12h3M12 2v3M12 19v3M5.6 5.6l2 2M16.4 16.4l2 2M5.6 18.4l2-2M16.4 7.6l2-2",
  messages: "M4 4h16v12H8l-4 4V4z",
  events: "M4 5h16v15H4V5zM4 9h16M8 3v4M16 3v4",
  blogs: "M6 3h12a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V5a2 2 0 012-2zM8 8h8M8 12h8M8 16h5",
  schedule: "M12 3a9 9 0 100 18 9 9 0 000-18zM12 7v5l3.5 2",
  genie: "M12 2l1.6 4.4L18 8l-4.4 1.6L12 14l-1.6-4.4L6 8l4.4-1.6L12 2zM5 15l.9 2.4L8.3 18l-2.4.9L5 21.3l-.9-2.4L1.7 18l2.4-.6L5 15zM19 14l.7 1.9L21.6 16.6l-1.9.7L19 19.2l-.7-1.9-1.9-.7 1.9-.6L19 14z",
  sharktank: "M3 17l5-5 4 3 6-7 3 3M3 21h18",
  ads: "M3 10v4h4l6 4V6L7 10H3zM17 8.5a4 4 0 010 7M20 6a7.5 7.5 0 010 12",
  image: "M4 5h16v14H4V5zM8.5 10a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM20 16l-5.5-5.5L9 16l-2.5-2.5L4 16",
  documents: "M7 3h7l5 5v13H7V3zM14 3v5h5M9 12h6M9 16h6",
  payments: "M3 6h18v12H3V6zM3 10h18M7 15h4",
  membership: "M12 2l2.6 5.3 5.9.8-4.3 4.1 1 5.8L12 15l-5.2 3 1-5.8-4.3-4.1 5.9-.8L12 2z",
  settings: "M12 15a3 3 0 100-6 3 3 0 000 6zM19.4 13.5a1.7 1.7 0 00.3 1.9l.1.1a2 2 0 11-2.9 2.9l-.1-.1a1.7 1.7 0 00-1.9-.3 1.7 1.7 0 00-1 1.6v.2a2 2 0 11-4 0v-.1a1.7 1.7 0 00-1.1-1.6 1.7 1.7 0 00-1.9.3l-.1.1a2 2 0 11-2.9-2.9l.1-.1a1.7 1.7 0 00.3-1.9 1.7 1.7 0 00-1.6-1H4a2 2 0 110-4h.1a1.7 1.7 0 001.6-1.1 1.7 1.7 0 00-.3-1.9l-.1-.1a2 2 0 112.9-2.9l.1.1a1.7 1.7 0 001.9.3H10a1.7 1.7 0 001-1.6V4a2 2 0 114 0v.1a1.7 1.7 0 001 1.6 1.7 1.7 0 001.9-.3l.1-.1a2 2 0 112.9 2.9l-.1.1a1.7 1.7 0 00-.3 1.9V10a1.7 1.7 0 001.6 1h.2a2 2 0 110 4h-.1a1.7 1.7 0 00-1.6 1z",
  help: "M12 21a9 9 0 100-18 9 9 0 000 18zM9.5 9a2.5 2.5 0 015 .3c0 1.7-2.5 2-2.5 3.7M12 17h.01",
  user: "M12 12a4.5 4.5 0 100-9 4.5 4.5 0 000 9zM4 21c0-4.4 3.6-7 8-7s8 2.6 8 7",
  shield: "M12 2l8 3.5v6c0 5-3.4 8.7-8 10.5-4.6-1.8-8-5.5-8-10.5v-6L12 2z",
  bell: "M6 9a6 6 0 1112 0c0 3.4.8 5 1.6 6H4.4C5.2 14 6 12.4 6 9zM9.5 19a2.5 2.5 0 005 0",
  logout: "M9 4H5v16h4M15 12H4M12 8l4 4-4 4M15 4h4v16h-4",
  menu: "M3 6h18M3 12h18M3 18h18",
  close: "M6 6l12 12M18 6L6 18",
  chevronDown: "M6 9l6 6 6-6",
  chevronRight: "M9 6l6 6-6 6",
  search: "M11 19a8 8 0 100-16 8 8 0 000 16zM21 21l-4.3-4.3",
};

export const NOTIF_ICON_BY_TYPE = {
  connection: "connections",
  meeting: "schedule",
  message: "messages",
  ad: "ads",
  payment: "payments",
  event: "events",
  sharktank: "sharktank",
  system: "settings",
};

/* Flat lookup: route path -> page title, used by the header breadcrumb. */
export const PAGE_TITLES = NAVIGATION
  .flatMap((s) => s.items)
  .reduce((acc, item) => {
    acc[item.path] = item.label;
    return acc;
  }, {});
